import React from "react";
import styled from "styled-components";
import { theme } from "../styles/theme";
import HomeIcon from "@mui/icons-material/Home";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PaymentsIcon from "@mui/icons-material/Payments";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ForumIcon from "@mui/icons-material/Forum";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import StarRateIcon from "@mui/icons-material/StarRate";
import eazyrent from "../assets/eazyrent.png";
import bookit from "../assets/bookit.png";

const AppsSection = styled.section`
  padding: 5rem 0;
  background-color: ${theme.colors.white};

  .container {
    text-align: center;
  }

  h2 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: ${theme.colors.primary};

    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.1rem;
    max-width: 700px;
    margin: 0 auto 3rem;
    color: ${theme.colors.gray};
  }
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AppCard = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  .app-image {
    height: 200px;
    background: linear-gradient(
      135deg,
      ${theme.colors.primary},
      ${theme.colors.secondary}
    );
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;

    .app-logo {
      width: 100px;
      height: 100px;
      background-color: ${theme.colors.white};
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2.5rem;
      color: ${theme.colors.primary};
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
      z-index: 2;
    }

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.2) 0%,
        rgba(255, 255, 255, 0) 70%
      );
      animation: rotate 20s linear infinite;
    }

    @keyframes rotate {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }

  .app-content {
    padding: 2rem;

    h3 {
      font-size: 1.5rem;
      margin-bottom: 1rem;
      color: ${theme.colors.primary};
    }

    p {
      color: ${theme.colors.gray};
      line-height: 1.6;
      margin-bottom: 1.5rem;
      text-align: left;
    }

    .app-features {
      text-align: left;
      margin-bottom: 1.5rem;

      li {
        margin-bottom: 0.5rem;
        color: ${theme.colors.gray};
        display: flex;
        align-items: center;

        svg {
          color: ${theme.colors.secondary};
          margin-right: 0.5rem;
          font-size: 1.2rem;
        }
      }
    }

    .app-buttons {
      display: flex;
      gap: 1rem;
      justify-content: center;

      a {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        border-radius: 5px;
        font-size: 0.9rem;
        font-weight: 500;
        transition: all 0.3s ease;

        &.app-store {
          background-color: #000;
          color: white;

          &:hover {
            background-color: #333;
          }
        }

        &.google-play {
          background-color: #000;
          color: white;

          &:hover {
            background-color: #333;
          }
        }
      }
    }
  }
`;

const AppsShowcase = () => {
  return (
    <AppsSection id="apps">
      <div className="container">
        <h2>Our Featured Apps</h2>
        <p>
          Discover our innovative applications designed to simplify your daily
          tasks and enhance your digital experience.
        </p>

        <AppsGrid>
          <AppCard>
            <div className="app-image">
              <div className="app-logo" style={{ backgroundColor: "black" }}>
                <img
                  src={eazyrent}
                  style={{ width: 50, height: 50, objectFit: "contain" }}
                  alt="eazyrent"
                />
              </div>
            </div>
            <div className="app-content">
              <h3>EazyRent</h3>
              <p>
                Streamline your rental experience with our comprehensive
                property management app. Find, list, and manage rentals with
                ease.
              </p>
              <ul className="app-features">
                <li>
                  <CheckCircleIcon /> Property listings with detailed
                  information
                </li>
                <li>
                  <PaymentsIcon /> Secure payment processing
                </li>
                <li>
                  <NotificationsActiveIcon /> Real-time notifications
                </li>
                <li>
                  <ForumIcon /> Tenant-landlord communication
                </li>
              </ul>
              <div className="app-buttons">
                <a
                  href="https://apps.apple.com/us/app/eazyrent-go/id6554008291"
                  className="app-store"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppleIcon /> App Store
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.easyrentsystems.app"
                  className="google-play"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AndroidIcon /> Google Play
                </a>
              </div>
            </div>
          </AppCard>

          <AppCard>
            <div className="app-image">
              <div className="app-logo" style={{ backgroundColor: "black" }}>
                <img
                  src={bookit}
                  style={{ width: 50, height: 50, objectFit: "contain" }}
                  alt="bookit"
                />
              </div>
            </div>
            <div className="app-content">
              <h3>BookIt</h3>
              <p>
                The ultimate booking solution for services and appointments.
                Schedule, manage, and track all your bookings in one place.
              </p>
              <ul className="app-features">
                <li>
                  <CalendarMonthIcon /> Easy appointment scheduling
                </li>
                <li>
                  <EventRepeatIcon /> Calendar integration
                </li>
                <li>
                  <NotificationsActiveIcon /> Automated reminders
                </li>
                <li>
                  <StarRateIcon /> Service provider ratings
                </li>
              </ul>
              <div className="app-buttons">
                <a
                  href="https://apps.apple.com/app/bookit-client/id6741794537"
                  className="app-store"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppleIcon /> App Store
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.bookitapp.systems"
                  className="google-play"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AndroidIcon /> Google Play
                </a>
              </div>
            </div>
          </AppCard>
        </AppsGrid>
      </div>
    </AppsSection>
  );
};

export default AppsShowcase;
